import _ from 'lodash';
import moment from 'moment';
import type { Channel } from 'phoenix';
import { Delta } from 'quill/core';
import type { Banner } from '../components/BannerModalContent';
import * as Constants from '../constants/redux';
import type SyncEngine from './SyncEngine';
import type Mark from './utils/Mark';
import { transformMark } from './utils/Mark';

interface RemoteSelections {
  [userId: string]: Mark;
}

export interface PresenceStateUser {
  id: string;
  index: number;
  name: string;
  avatar: { thumb: string | null };
}

export interface PresenceState {
  metas: { edit: boolean; active: boolean }[];
  user: PresenceStateUser;
}

export const CREATION_ID = 'create' as const;

export type MagneticWallType = 'comment' | 'annotation' | 'reaction';

export type MagneticWallFocus = {
  id: string;
  type: MagneticWallType;
  active: boolean;
};

type MagneticWallHighlight = {
  id: string;
  type: MagneticWallType;
};

export interface EditorState {
  channel: Channel | null;
  sync: SyncEngine | null;
  syncStatus: string;
  saveStatus: string;
  postId: string | null;
  presence: { [userId: string]: PresenceState };
  title: string;
  editable: boolean;
  locked: boolean;
  readOnly: boolean;
  pendingTransmitRange: unknown | null;
  remoteSelections: RemoteSelections;
  initialContent: Delta | null;
  editingLink: boolean;
  pickingReaction: boolean;
  magneticWallFocus: MagneticWallFocus | null;
  magneticWallHighlight: MagneticWallHighlight | null;
  version: number;
  unsaved: Delta;
  hasStateBar: boolean;
  isPostBannerPopOverOpen: boolean;
  isPositioningPostBanner: boolean;
  currentPostBanner: Banner | null;
  selection?: unknown;
  updatedAt?: moment.Moment;
}

const initialState: EditorState = {
  channel: null,
  sync: null,
  syncStatus: 'down', // Controlled by sync engine
  saveStatus: '', // Independent timer mechanism from sync engine
  postId: null,
  presence: {},
  title: '',
  editable: true, // Inherent editability, ex permissions, cannot change
  locked: false, // Editability due to tandem error, can change if recovered from error
  readOnly: true, // User specified editability
  pendingTransmitRange: null,
  remoteSelections: {},
  initialContent: null,
  editingLink: false,
  pickingReaction: false,
  magneticWallFocus: null,
  magneticWallHighlight: null,
  version: -1,
  unsaved: new Delta(),
  hasStateBar: false,
  isPostBannerPopOverOpen: false,
  isPositioningPostBanner: false,
  currentPostBanner: null,
};

// TODO: export from namespaces fall within that rule, remove once namespace is changed
/* eslint-disable import/no-unused-modules */

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Actions {
  export interface EDITOR_LEAVE {
    type: typeof Constants.EDITOR_LEAVE;
  }

  export interface EDITOR_LOAD {
    type: typeof Constants.EDITOR_LOAD;
    channel: Channel;
    title: string;
    content: Delta | null;
    editable: boolean;
    postId: string | null;
    readOnly: boolean;
    sync: SyncEngine;
  }

  export interface EDITOR_LOCK {
    type: typeof Constants.EDITOR_LOCK;
  }

  export interface EDITOR_READ_ONLY {
    type: typeof Constants.EDITOR_READ_ONLY;
    readOnly: boolean;
  }

  export interface EDITOR_PRESENCE {
    type: typeof Constants.EDITOR_PRESENCE;
    presence: { [userId: string]: PresenceState };
  }

  export interface EDITOR_LOCAL_UPDATED {
    type: typeof Constants.EDITOR_LOCAL_UPDATED;
    delta: Delta;
  }

  export interface EDITOR_REMOTE_UPDATED {
    type: typeof Constants.EDITOR_REMOTE_UPDATED;
    delta: Delta;
  }

  export interface EDITOR_TRANSMIT_STATE_CHANGE {
    type: typeof Constants.EDITOR_TRANSMIT_STATE_CHANGE;
    pendingTransmitRange: unknown;
  }

  export interface EDITOR_SELECTION_TRANSMITTED {
    type: typeof Constants.EDITOR_SELECTION_TRANSMITTED;
  }

  export interface EDITOR_SELECTION_REMOTE {
    type: typeof Constants.EDITOR_SELECTION_REMOTE;
    userId: string;
    mark: Mark;
  }

  export interface EDITOR_FOCUS_COMMENT_THREAD {
    type: typeof Constants.EDITOR_FOCUS_COMMENT_THREAD;
    id: string;
    isActive: boolean;
  }

  export interface EDITOR_FOCUS_REACTION {
    type: typeof Constants.EDITOR_FOCUS_REACTION;
    id: string;
    isActive: boolean;
  }

  export interface EDITOR_FOCUS_ANNOTATION {
    type: typeof Constants.EDITOR_FOCUS_ANNOTATION;
    id: string;
    isActive: boolean;
  }

  export interface EDITOR_BLUR_MAGNETIC_WALL {
    type: typeof Constants.EDITOR_BLUR_MAGNETIC_WALL;
  }

  export interface EDITOR_HIGHLIGHT_ANNOTATION {
    type: typeof Constants.EDITOR_HIGHLIGHT_ANNOTATION;
    id: string;
  }

  export interface EDITOR_HIGHLIGHT_COMMENT_THREAD {
    type: typeof Constants.EDITOR_HIGHLIGHT_COMMENT_THREAD;
    id: string;
  }

  export interface EDITOR_HIGHLIGHT_REACTION {
    type: typeof Constants.EDITOR_HIGHLIGHT_REACTION;
    id: string;
  }

  export interface EDITOR_REMOVE_MAGNETIC_WALL_HIGHLIGHT {
    type: typeof Constants.EDITOR_REMOVE_MAGNETIC_WALL_HIGHLIGHT;
  }

  export interface EDITOR_EDITING_LINK {
    type: typeof Constants.EDITOR_EDITING_LINK;
    editingLink: boolean;
  }

  export interface EDITOR_PICKING_REACTION {
    type: typeof Constants.EDITOR_PICKING_REACTION;
    pickingReaction: boolean;
  }

  export interface EDITOR_TITLE_UPDATE {
    type: typeof Constants.EDITOR_TITLE_UPDATE;
    title: string;
  }

  export interface EDITOR_SAVE_STATUS_CHANGE {
    type: typeof Constants.EDITOR_SAVE_STATUS_CHANGE;
    saveStatus: string;
  }

  export interface EDITOR_SYNC_STATE_CHANGE {
    type: typeof Constants.EDITOR_SYNC_STATE_CHANGE;
    version: number;
    unsaved: Delta;
  }

  export interface EDITOR_SYNC_STATUS_CHANGE {
    type: typeof Constants.EDITOR_SYNC_STATUS_CHANGE;
    syncStatus: string;
  }

  export interface EDITOR_SET_HAS_STATE_BAR {
    type: typeof Constants.EDITOR_SET_HAS_STATE_BAR;
    hasStateBar: boolean;
  }

  export interface EDITOR_SET_POST_BANNER_POPOVER_OPEN {
    type: typeof Constants.EDITOR_SET_POST_BANNER_POPOVER_OPEN;
    isPostBannerPopOverOpen: boolean;
  }

  export interface EDITOR_SET_IS_POSITIONING_POST_BANNER {
    type: typeof Constants.EDITOR_SET_IS_POSITIONING_POST_BANNER;
    isPositioningPostBanner: boolean;
  }

  export interface EDITOR_SET_CURRENT_POST_BANNER {
    type: typeof Constants.EDITOR_SET_CURRENT_POST_BANNER;
    currentPostBanner: Banner | null;
  }

  export interface OTHER {
    type?: undefined;
  }

  export type ANY =
    | EDITOR_LEAVE
    | EDITOR_LOAD
    | EDITOR_LOCK
    | EDITOR_READ_ONLY
    | EDITOR_PRESENCE
    | EDITOR_LOCAL_UPDATED
    | EDITOR_REMOTE_UPDATED
    | EDITOR_TRANSMIT_STATE_CHANGE
    | EDITOR_SELECTION_TRANSMITTED
    | EDITOR_SELECTION_REMOTE
    | EDITOR_FOCUS_ANNOTATION
    | EDITOR_FOCUS_COMMENT_THREAD
    | EDITOR_FOCUS_REACTION
    | EDITOR_BLUR_MAGNETIC_WALL
    | EDITOR_HIGHLIGHT_ANNOTATION
    | EDITOR_HIGHLIGHT_COMMENT_THREAD
    | EDITOR_HIGHLIGHT_REACTION
    | EDITOR_REMOVE_MAGNETIC_WALL_HIGHLIGHT
    | EDITOR_EDITING_LINK
    | EDITOR_PICKING_REACTION
    | EDITOR_TITLE_UPDATE
    | EDITOR_SAVE_STATUS_CHANGE
    | EDITOR_SYNC_STATE_CHANGE
    | EDITOR_SYNC_STATUS_CHANGE
    | EDITOR_SET_HAS_STATE_BAR
    | EDITOR_SET_POST_BANNER_POPOVER_OPEN
    | EDITOR_SET_IS_POSITIONING_POST_BANNER
    | EDITOR_SET_CURRENT_POST_BANNER
    | OTHER;
}

export default function editorReducer(
  state = initialState,
  action: Actions.ANY = {},
): EditorState {
  switch (action.type) {
    case Constants.EDITOR_LEAVE: {
      return initialState;
    }
    case Constants.EDITOR_LOAD: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { channel, title, content, editable, postId, readOnly, sync } =
        action;
      return {
        ...state,
        postId,
        channel,
        sync,
        title,
        editable,
        readOnly: readOnly || !editable,
        selection: initialState.selection,
        initialContent: content,
      };
    }
    case Constants.EDITOR_LOCK: {
      return {
        ...state,
        locked: true,
        readOnly: true,
      };
    }
    case Constants.EDITOR_READ_ONLY: {
      const { readOnly } = action;
      return {
        ...state,
        readOnly: readOnly || state.locked || !state.editable,
        isPositioningPostBanner: false,
      };
    }
    case Constants.EDITOR_PRESENCE: {
      const { presence } = action;

      const editors = Object.keys(presence).filter((id) =>
        presence[id]?.metas.some((meta) => meta.edit),
      );

      const nonEditingSelectionIds = Object.keys(state.remoteSelections).filter(
        (id) => !editors.includes(id),
      );

      const remoteSelections = nonEditingSelectionIds.length
        ? _.omit(state.remoteSelections, nonEditingSelectionIds)
        : state.remoteSelections;

      return {
        ...state,
        remoteSelections,
        presence,
      };
    }
    case Constants.EDITOR_LOCAL_UPDATED: {
      const { delta } = action;
      return {
        ...state,
        updatedAt: moment(),
        remoteSelections: transformRemoteSelections(
          state.remoteSelections,
          delta,
          true,
        ),
      };
    }
    case Constants.EDITOR_REMOTE_UPDATED: {
      const { delta } = action;
      return {
        ...state,
        updatedAt: moment(),
        remoteSelections: transformRemoteSelections(
          state.remoteSelections,
          delta,
        ),
      };
    }
    case Constants.EDITOR_TRANSMIT_STATE_CHANGE: {
      const { pendingTransmitRange } = action;
      return {
        ...state,
        pendingTransmitRange,
      };
    }
    case Constants.EDITOR_SELECTION_TRANSMITTED: {
      return {
        ...state,
        pendingTransmitRange: null,
      };
    }
    case Constants.EDITOR_SELECTION_REMOTE: {
      const { userId, mark } = action;
      const transformed = mark && transformMark(mark, state.unsaved);

      return {
        ...state,
        remoteSelections: transformed
          ? { ...state.remoteSelections, [userId]: transformed }
          : _.omit(state.remoteSelections, userId),
      };
    }
    case Constants.EDITOR_FOCUS_ANNOTATION: {
      const { id, isActive } = action;
      return {
        ...state,
        magneticWallFocus: { id, active: isActive, type: 'annotation' },
      };
    }
    case Constants.EDITOR_FOCUS_REACTION: {
      const { id, isActive } = action;
      return {
        ...state,
        magneticWallFocus: { id, active: isActive, type: 'reaction' },
      };
    }
    case Constants.EDITOR_FOCUS_COMMENT_THREAD: {
      const { id, isActive } = action;
      return {
        ...state,
        magneticWallFocus: { id, active: isActive, type: 'comment' },
      };
    }
    case Constants.EDITOR_BLUR_MAGNETIC_WALL: {
      return {
        ...state,
        magneticWallFocus: null,
      };
    }
    case Constants.EDITOR_HIGHLIGHT_ANNOTATION: {
      const { id } = action;
      return {
        ...state,
        magneticWallHighlight: { id, type: 'annotation' },
      };
    }
    case Constants.EDITOR_HIGHLIGHT_COMMENT_THREAD: {
      const { id } = action;
      return {
        ...state,
        magneticWallHighlight: { id, type: 'comment' },
      };
    }
    case Constants.EDITOR_HIGHLIGHT_REACTION: {
      const { id } = action;
      return {
        ...state,
        magneticWallHighlight: { id, type: 'reaction' },
      };
    }
    case Constants.EDITOR_REMOVE_MAGNETIC_WALL_HIGHLIGHT: {
      return {
        ...state,
        magneticWallHighlight: null,
      };
    }

    case Constants.EDITOR_EDITING_LINK: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { editingLink } = action;
      return {
        ...state,
        editingLink,
      };
    }
    case Constants.EDITOR_PICKING_REACTION: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { pickingReaction } = action;
      return {
        ...state,
        pickingReaction,
      };
    }
    case Constants.EDITOR_TITLE_UPDATE: {
      const { title } = action;
      return {
        ...state,
        title,
      };
    }
    case Constants.EDITOR_SAVE_STATUS_CHANGE: {
      const { saveStatus } = action;
      return {
        ...state,
        saveStatus,
      };
    }
    case Constants.EDITOR_SYNC_STATE_CHANGE: {
      const { version, unsaved } = action;
      return {
        ...state,
        version,
        unsaved,
      };
    }
    case Constants.EDITOR_SYNC_STATUS_CHANGE: {
      // This action added mainly for visibility/debuggability.  It is
      // not the basis for UI feedback about connection status.
      const { syncStatus } = action;
      return {
        ...state,
        syncStatus,
      };
    }
    case Constants.EDITOR_SET_HAS_STATE_BAR: {
      return {
        ...state,
        hasStateBar: action.hasStateBar,
      };
    }
    case Constants.EDITOR_SET_POST_BANNER_POPOVER_OPEN: {
      return {
        ...state,
        isPostBannerPopOverOpen: action.isPostBannerPopOverOpen,
        readOnly: false,
      };
    }
    case Constants.EDITOR_SET_IS_POSITIONING_POST_BANNER: {
      return {
        ...state,
        isPositioningPostBanner: action.isPositioningPostBanner,
        readOnly: false,
      };
    }
    case Constants.EDITOR_SET_CURRENT_POST_BANNER: {
      return {
        ...state,
        currentPostBanner: action.currentPostBanner,
      };
    }
    default:
      return state;
  }
}

function compactObject<T>(object: { [key: string]: T | null | undefined }): {
  [key: string]: T;
} {
  const compacted: { [key: string]: T } = {};
  Object.entries(object).forEach(([key, value]) => {
    if (value != null) {
      compacted[key] = value;
    }
  });
  return compacted;
}

function transformRemoteSelections(
  remoteSelections: RemoteSelections,
  delta: Delta,
  priority = false,
) {
  const transformed = compactObject(
    _.reduce<RemoteSelections, { [userId: string]: Mark | null }>(
      remoteSelections,
      (memo, mark, userId) => {
        // eslint-disable-next-line no-param-reassign
        memo[userId] = transformMark(mark, delta, priority);
        return memo;
      },
      {},
    ),
  );
  return _.isEqual(transformed, remoteSelections)
    ? remoteSelections
    : transformed;
}
